// Where the till's cash goes (UC-61, UC-62; brief T32), as its order store
// says it, for Till settings' Cash point row (T26) and the cash-point step,
// in T26's Sheet and in the onboarding (T27).
//
// The store answers GET /store/cash-point with the server's answer it kept
// from its last menu check and the shift state it heard from each other till
// on the till mesh, and sends a manager's choice on PUT /store/cash-point to
// the server, keeping its answer (internal/orderstore/cashpoint.go, native
// level 14). Every rule is the server's and the store's: the screen offers
// this till and the shop's cash points, and says each refusal in plain
// words. Another till is sent only once the link test to it answered and
// this till heard that till's shift state (decision
// 2026-10-22-onboarding-tests-the-link), which a cash point on an older app
// never says, and never to a till the mesh lists on an older app. The screen
// keeps nothing of it in the browser.

import { store } from '../api/client';
import type { components } from '../api/store';
import type { MessageKey } from '../i18n/messages';
import type { LinkAnswer, Mesh } from './mesh.svelte';

export type CashPointBody = components['schemas']['StoreCashPointBody'];

/** How long a read or a choice waits before the screen says it is waiting. */
export const CASH_POINT_SLOW_MS = 300;

/**
 * How many times the step reads the store for the picked till's shift state
 * once its link test answered, and how long it waits between them. The link
 * test hears the other till, but a cash point says its shift state only in
 * its hint, every 25 to 35 seconds (hintDelay in internal/tillmesh/push.go),
 * and not in its first answer to a till it did not know: so the reads cover
 * the longest hint wait, as on a till whose mesh just started.
 */
export const HEARD_TRIES = 20;
export const HEARD_AGAIN_MS = 2000;

/** Where a picked other till stands: its link to test, its shift state to hear, ready to send, or on an older app. */
export type CashPointGate = 'test' | 'hearing' | 'ready' | 'older_app';

/** One choice of the step: this till, or one of the shop's cash points. */
export interface CashPointChoice {
	tillId: string;
	/** The till's name; empty for this till, which reads "This till". */
	name: string;
	self: boolean;
}

/**
 * The step's choices: this till first, then each other till the store says
 * is a cash point of the shop, as the server last said, in its order.
 */
export function cashPointChoices(body: CashPointBody | null, selfId: string): CashPointChoice[] {
	const others = (body?.options ?? []).filter((till) => till.tillId !== selfId);
	return [
		{ tillId: selfId, name: '', self: true },
		...others.map((till) => ({ tillId: till.tillId, name: till.name, self: false })),
	];
}

/** Whether this till heard that till's shift state on the mesh, at any time. */
export function heardFrom(body: CashPointBody | null, tillId: string): boolean {
	return (body?.heard ?? []).some((till) => till.tillId === tillId);
}

/**
 * A refused choice in plain words: this till's own open shift, a till other
 * tills send their cash to, a till that sends its cash elsewhere, a till no
 * longer of the shop, a person who is not a manager, or no answer from Bosla
 * (a 502, 503 or 504 that names no code, such as the store's empty 502).
 * Anything else did not go through.
 */
export function cashPointRefusal(status: number | null, code?: string): MessageKey {
	if (status === 409 && code === 'SHIFT_OPEN_HERE') return 'cashPointOpenHere';
	if (status === 409 && code === 'CASH_POINT_IN_USE') return 'cashPointInUse';
	if (status === 422 && code === 'CASH_POINT_NOT_ALLOWED') return 'cashPointNotAllowed';
	if (status === 404 && code === 'NOT_FOUND') return 'linkGone';
	if (status === 403 && code === 'FORBIDDEN') return 'cashPointForbidden';
	if ((status === 502 || status === 503 || status === 504) && code === undefined)
		return 'cashPointNeedsInternet';
	return 'printerFailed';
}

/** The store's cash point as the screen last read it, and a manager's pick. */
export class TillCashPoint {
	/** The store's last good answer; null until one came. */
	body = $state<CashPointBody | null>(null);
	/** A read is on its way, and past CASH_POINT_SLOW_MS once `slow`. */
	loading = $state(false);
	slow = $state(false);
	/** The last read got no answer or a refusal. */
	failed = $state(false);
	/** The failure was no answer or a 5xx, the only ones Retry is for (.claude/rules/web.md). */
	retryable = $state(false);
	/** A read finished, answered or not, so the step can say what it holds. */
	settled = $state(false);
	/** The till the manager picked; null while it is the one the store keeps. */
	picked = $state<string | null>(null);
	/** Where the picked till stands. */
	gate = $state<CashPointGate>('test');
	/** A choice is on its way to the store, and past CASH_POINT_SLOW_MS once `savingSlow`. */
	saving = $state(false);
	savingSlow = $state(false);
	/** The last choice's refusal, in words; null while there is none. */
	refusal = $state<MessageKey | null>(null);

	/** The mesh as the step last read it, for the tills on an older app. */
	readonly #mesh: { readonly mesh: Mesh | null } | undefined;
	/** This till's own id, as the last pick named it. */
	#self = '';
	// Reads can overlap: an answer older than one already taken is dropped. A
	// new pick, or a new link test, ends the wait for the old one's shift state.
	#started = 0;
	#taken = 0;
	#reading = 0;
	#hearing = 0;

	constructor(mesh?: { readonly mesh: Mesh | null }) {
		this.#mesh = mesh;
	}

	/** The till the store says this till's cash goes to; null before any answer. */
	get kept(): string | null {
		return this.body?.cashPoint.tillId ?? null;
	}

	/** The till the step shows as chosen. */
	get choice(): string | null {
		return this.picked ?? this.kept;
	}

	/** The manager picked another till than the one kept. */
	get changed(): boolean {
		return this.picked !== null && this.picked !== this.kept;
	}

	/**
	 * The picked till runs an older Bosla app: its shift state was never
	 * heard, or the mesh lists it as speaking the old format.
	 */
	get olderApp(): boolean {
		if (!this.changed || this.picked === this.#self) return false;
		if (this.gate === 'older_app') return true;
		const till = this.#mesh?.mesh?.tills.find((t) => t.tillId === this.picked);
		return till?.speaks === 'legacy';
	}

	/**
	 * What the step shows can stand: a pick Confirm can send, or, with no
	 * pick, a kept cash point the owner did not remove.
	 */
	get ready(): boolean {
		if (this.changed) return this.gate === 'ready' && !this.olderApp;
		return !(this.body?.cashPoint.removed ?? false);
	}

	#take(mine: number, body: CashPointBody): void {
		this.#taken = mine;
		this.body = body;
		if (this.picked === this.kept) this.picked = null;
		// A picked till no longer among the shop's cash points is no pick.
		if (
			this.picked !== null &&
			this.picked !== this.#self &&
			!body.options.some((till) => till.tillId === this.picked)
		)
			this.#drop();
	}

	#drop(): void {
		this.#hearing++;
		this.picked = null;
		this.gate = 'test';
		this.refusal = null;
	}

	/** Read the cash point from the store again; it never throws. */
	async read(): Promise<void> {
		const mine = ++this.#started;
		this.#reading = mine;
		this.loading = true;
		this.slow = false;
		const timer = setTimeout(() => {
			if (this.#reading === mine) this.slow = true;
		}, CASH_POINT_SLOW_MS);
		try {
			const { data, error, response } = await store.GET('/store/cash-point');
			if (mine < this.#taken) return;
			this.failed = !(error === undefined && data && response.status === 200);
			this.retryable = this.failed && response.status >= 500;
			if (!this.failed && data) this.#take(mine, data);
			else this.#taken = mine;
		} catch {
			if (mine >= this.#taken) {
				this.#taken = mine;
				this.failed = true;
				this.retryable = true;
			}
		} finally {
			clearTimeout(timer);
			this.settled = true;
			if (this.#reading === mine) {
				this.loading = false;
				this.slow = false;
			}
		}
	}

	/** The manager picks a till: this till is ready at once, another waits for its link test. */
	pick(tillId: string, selfId: string): void {
		if (this.saving) return;
		this.#self = selfId;
		this.#hearing++;
		this.refusal = null;
		this.picked = tillId === this.kept ? null : tillId;
		this.gate = tillId === selfId ? 'ready' : 'test';
	}

	/** A link test to the picked till starts: until it answers, Confirm sends nothing. */
	retest(tillId: string): void {
		if (tillId !== this.picked || this.gate === 'test') return;
		this.#hearing++;
		this.gate = 'test';
	}

	/**
	 * The picked till's link test answered: read the store until it says it
	 * heard that till's shift state, at most HEARD_TRIES times. A till never
	 * heard across reads that all answered runs an older Bosla app; a read
	 * that failed proves nothing, so the link is left to test again. Any other
	 * answer leaves the link to test.
	 */
	async tested(tillId: string, answer: LinkAnswer): Promise<void> {
		if (tillId !== this.picked) return;
		const mine = ++this.#hearing;
		if (answer !== 'answered') {
			this.gate = 'test';
			return;
		}
		this.gate = 'hearing';
		let answered = 0;
		for (let tries = 1; tries <= HEARD_TRIES; tries++) {
			await this.read();
			if (mine !== this.#hearing) return;
			if (!this.failed) {
				answered++;
				if (heardFrom(this.body, tillId)) {
					this.gate = 'ready';
					return;
				}
			}
			if (tries < HEARD_TRIES) await new Promise((resolve) => setTimeout(resolve, HEARD_AGAIN_MS));
			if (mine !== this.#hearing) return;
		}
		this.gate = answered === HEARD_TRIES ? 'older_app' : 'test';
	}

	/** Drop the pick, as Close, Back and Skip do; the kept cash point stays. */
	reset(): void {
		if (this.saving) return;
		this.#drop();
	}

	/**
	 * Send the pick to the store. True once nothing is left to send: the
	 * store took it, or the pick is the kept one. A refusal stays in words,
	 * with the pick. The store's answer is newer than every read on its way,
	 * which are then dropped.
	 */
	async save(): Promise<boolean> {
		if (!this.changed) return true;
		if (this.saving || !this.ready || this.picked === null) return false;
		this.saving = true;
		this.savingSlow = false;
		this.refusal = null;
		const timer = setTimeout(() => (this.savingSlow = true), CASH_POINT_SLOW_MS);
		try {
			const { data, error, response } = await store.PUT('/store/cash-point', {
				body: { cashPointTillId: this.picked },
			});
			if (error === undefined && data && response.status === 200) {
				// Past every read started so far, so none on its way is taken.
				this.#taken = ++this.#started;
				this.body = data;
				this.failed = false;
				this.settled = true;
				this.#drop();
				return true;
			}
			this.refusal = cashPointRefusal(response.status, error?.code);
		} catch {
			this.refusal = 'printerFailed';
		} finally {
			clearTimeout(timer);
			this.saving = false;
			this.savingSlow = false;
		}
		return false;
	}
}
